import React, { useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import SearchIcon from '@mui/icons-material/Search'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material'
import { PMJournalHeaderType, ListFilter, AI_JOURNAL, YE_JOURNAL, EMPTY_FILTER, EMPTY_GUID } from './PMJournal.types'
import { filterJournals } from './PMJournal.utils'

interface Props {
    journals: PMJournalHeaderType[]
    listLoading: boolean
    listFrom: Date | null
    setListFrom: (v: Date | null) => void
    listTo: Date | null
    setListTo: (v: Date | null) => void
    listFilter: ListFilter
    setListFilter: React.Dispatch<React.SetStateAction<ListFilter>>
    errorMsg: string | null
    openNewJournal: () => void
    openExistingJournal: (header: PMJournalHeaderType) => void
    projectName: (id: string) => string
}

const PMJournalList = ({
    journals, listLoading,
    listFrom, setListFrom,
    listTo, setListTo,
    listFilter, setListFilter,
    errorMsg, openNewJournal, openExistingJournal, projectName,
}: Props) => {
    const { t, i18n } = useTranslation()
    const theme = useTheme()
    const dateLocale = i18n.language === 'de' ? de : enUS

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    type SortKey = 'date' | 'project' | 'journalName' | 'totalHours' | 'status'
    type SortDir = 'asc' | 'desc'
    const [sortKey, setSortKey] = useState<SortKey>('date')
    const [sortDir, setSortDir] = useState<SortDir>('desc')

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(key)
            setSortDir('asc')
        }
        setPage(0)
    }

    const setListFilterAndReset = (fn: React.SetStateAction<ListFilter>) => {
        setPage(0)
        setListFilter(fn)
    }

    const isFilterActive = !!(listFilter.journalId || listFilter.project || listFilter.type || listFilter.status || listFilter.role)

    const filteredJournals = filterJournals(journals, listFilter, projectName)
    const sortedJournals = [...filteredJournals].sort((a, b) => {
        let v = 0
        switch (sortKey) {
            case 'date':        v = new Date(a.date).getTime() - new Date(b.date).getTime(); break
            case 'project':     v = projectName(a.projectId).localeCompare(projectName(b.projectId)); break
            case 'journalName': v = a.journalName.localeCompare(b.journalName); break
            case 'totalHours':  v = (a.totalHours ?? 0) - (b.totalHours ?? 0); break
            case 'status': {
                const order = (j: PMJournalHeaderType) => j.posted ? 2 : (j.projJournalTableEntityId && j.projJournalTableEntityId !== EMPTY_GUID) ? 1 : 0
                v = order(a) - order(b); break
            }
        }
        return sortDir === 'asc' ? v : -v
    })
    const pagedJournals = sortedJournals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    return (
        <>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5">{t('pmJournal.title')}</Typography>
                    <Button variant="contained" size="small" onClick={openNewJournal}>
                        {t('pmJournal.new')}
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1, mt: 2 }}>
                        <DatePicker
                            label={t('pmJournal.from')}
                            value={listFrom}
                            onChange={(v) => setListFrom(v)}
                            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                        />
                        <DatePicker
                            label={t('pmJournal.to')}
                            value={listTo}
                            onChange={(v) => setListTo(v)}
                            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            value={listFilter.journalId}
                            onChange={(e) => setListFilterAndReset(f => ({ ...f, journalId: e.target.value }))}
                            placeholder={t('pmJournal.journalId')}
                            sx={{ width: 140 }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> } }}
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            value={listFilter.project}
                            onChange={(e) => setListFilterAndReset(f => ({ ...f, project: e.target.value }))}
                            placeholder={t('pmJournal.project')}
                            sx={{ width: 200 }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> } }}
                        />
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            value={listFilter.type}
                            onChange={(e) => setListFilterAndReset(f => ({ ...f, type: e.target.value }))}
                            label={t('pmJournal.type')}
                            sx={{ width: 130 }}
                        >
                            <MenuItem value="">{t('pmJournal.filterAll')}</MenuItem>
                            <MenuItem value={AI_JOURNAL}>{t('pmJournal.aiFteLabel')}</MenuItem>
                            <MenuItem value={YE_JOURNAL}>{t('pmJournal.yeLabel')}</MenuItem>
                        </TextField>
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            value={listFilter.role}
                            onChange={(e) => setListFilterAndReset(f => ({ ...f, role: e.target.value }))}
                            label={t('pmJournal.deputyColumn')}
                            sx={{ width: 140 }}
                        >
                            <MenuItem value="">{t('pmJournal.filterAll')}</MenuItem>
                            <MenuItem value="pm">{t('pmJournal.owner')}</MenuItem>
                            <MenuItem value="deputy">{t('pmJournal.deputy')}</MenuItem>
                        </TextField>
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            value={listFilter.status}
                            onChange={(e) => setListFilterAndReset(f => ({ ...f, status: e.target.value }))}
                            label={t('pmJournal.statusLabel')}
                            sx={{ width: 130 }}
                        >
                            <MenuItem value="">{t('pmJournal.filterAll')}</MenuItem>
                            <MenuItem value="posted">{t('pmJournal.posted')}</MenuItem>
                            <MenuItem value="saved">{t('pmJournal.saved')}</MenuItem>
                        </TextField>
                        {isFilterActive && (
                            <Tooltip title={t('pmJournal.clearFilter')}>
                                <IconButton size="small" onClick={() => { setPage(0); setListFilter(EMPTY_FILTER) }}>
                                    <FilterAltOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </LocalizationProvider>

                {errorMsg && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {errorMsg}
                    </Typography>
                )}

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                                <TableCell sortDirection={sortKey === 'date' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'date'} direction={sortKey === 'date' ? sortDir : 'asc'} onClick={() => handleSort('date')}>
                                        {t('pmJournal.date')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{t('pmJournal.journalId')}</TableCell>
                                <TableCell sortDirection={sortKey === 'project' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'project'} direction={sortKey === 'project' ? sortDir : 'asc'} onClick={() => handleSort('project')}>
                                        {t('pmJournal.project')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortKey === 'journalName' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'journalName'} direction={sortKey === 'journalName' ? sortDir : 'asc'} onClick={() => handleSort('journalName')}>
                                        {t('pmJournal.type')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: 90 }} sortDirection={sortKey === 'totalHours' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'totalHours'} direction={sortKey === 'totalHours' ? sortDir : 'asc'} onClick={() => handleSort('totalHours')}>
                                        {t('pmJournal.hours')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{t('pmJournal.deputyColumn')}</TableCell>
                                <TableCell sortDirection={sortKey === 'status' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'status'} direction={sortKey === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                                        {t('pmJournal.statusLabel')}
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listLoading && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!listLoading && journals.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                        {t('pmJournal.noJournals')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {!listLoading && pagedJournals.map((j, idx) => (
                                <TableRow
                                    key={j.projJournalTableEntityId ?? idx}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => openExistingJournal(j)}
                                >
                                    <TableCell>
                                        {j.date
                                            ? format(new Date(j.date), i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy')
                                            : '–'}
                                    </TableCell>
                                    <TableCell>{j.batchnumber ?? '–'}</TableCell>
                                    <TableCell>{projectName(j.projectId)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={j.journalName === AI_JOURNAL ? t('pmJournal.aiFteLabel') : t('pmJournal.yeLabel')}
                                            size="small"
                                            variant="outlined"
                                            sx={j.journalName === AI_JOURNAL
                                                ? { borderColor: '#8371F0', color: '#8371F0' }
                                                : { borderColor: '#198585', color: '#198585' }
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: 90 }}>
                                        {j.totalHours != null
                                            ? `${j.totalHours.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`
                                            : '–'}
                                    </TableCell>
                                    <TableCell>
                                        {j.isDeputyView
                                            ? <Chip label={t('pmJournal.deputy')} color="info" size="small" variant="outlined" />
                                            : <Chip label={t('pmJournal.owner')} color="default" size="small" variant="outlined" />
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {j.posted
                                            ? <Chip label={t('pmJournal.posted')} color="success" size="small" />
                                            : (j.projJournalTableEntityId && j.projJournalTableEntityId !== EMPTY_GUID)
                                                ? <Chip label={t('pmJournal.saved')} color="success" size="small" variant="outlined" />
                                                : <Chip label={t('pmJournal.unposted')} color="default" size="small" />
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredJournals.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0) }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('pmJournal.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('pmJournal.of')} ${count}`}
                />
            </DialogContent>
        </>
    )
}

export default PMJournalList
